import { useCallback, useEffect, useRef, useState } from "react";
import { BRIDGE, isAllowedOrigin } from "./tokens";

// ===========================================================================
// SectionBridge — parent-side postMessage handler for the section bridge.
//
// Owns the handshake and the trusted-origin gate, exposes the section list as
// state, and returns imperative commands the editor sends into the iframe
// (setEditMode / highlight / flash / scrollTo / applyEdit / revert).
//
// Security: every inbound message is checked against `allowlist` (via
// isAllowedOrigin) AND must echo the per-mount token we handed the bridge in
// the init reply. Anything else is dropped silently.
// ===========================================================================

function makeToken() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "vb_" + Math.abs(Date.now() ^ (Math.floor(performance.now?.() || 0) * 997)).toString(36);
}

export function useSectionBridge({
  iframeRef,
  allowlist,
  onSectionClick,
  onSectionHover,
  onReady,
} = {}) {
  const [sections, setSections] = useState([]);
  const [ready, setReady] = useState(false);
  const tokenRef = useRef(makeToken());
  // Latest-callback refs so the message listener stays stable (mounts once).
  const cbRef = useRef({});
  cbRef.current = { onSectionClick, onSectionHover, onReady };

  // Post a command into the iframe, targeting its own origin when known.
  const send = useCallback(
    (type, extra) => {
      const win = iframeRef?.current?.contentWindow;
      if (!win) return;
      let target = "*";
      try {
        const src = iframeRef.current.getAttribute("src");
        if (src) target = new URL(src, window.location.href).origin;
      } catch {
        /* srcDoc / relative — fall back to "*", the bridge still origin-checks us */
      }
      win.postMessage({ type, ...(extra || {}) }, target);
    },
    [iframeRef]
  );

  useEffect(() => {
    function onMessage(e) {
      const d = e.data || {};
      // Scope this hook to ITS OWN iframe. Multiple bridged iframes can live on
      // one page (e.g. the before/after compare overlay); pairing by source
      // window keeps each hook talking only to its frame, so their handshakes
      // and section streams never cross.
      const win = iframeRef?.current?.contentWindow;
      if (win && e.source !== win) return;
      // The ready handshake is the only message that predates the token, and
      // it must originate from a trusted origin.
      if (d.type === BRIDGE.READY) {
        if (!isAllowedOrigin(e.origin, allowlist)) return;
        setReady(true);
        // Reply with the token + our origin so the bridge can pin us.
        send(BRIDGE.INIT, { token: tokenRef.current, origin: window.location.origin });
        cbRef.current.onReady && cbRef.current.onReady();
        return;
      }
      // Everything else must pass origin + token.
      if (!isAllowedOrigin(e.origin, allowlist)) return;
      if (d.token && d.token !== tokenRef.current) return;
      switch (d.type) {
        case BRIDGE.SECTIONS:
          if (Array.isArray(d.list)) setSections(d.list);
          break;
        case BRIDGE.SECTION_CLICK:
          cbRef.current.onSectionClick && cbRef.current.onSectionClick(d.id);
          break;
        case BRIDGE.SECTION_HOVER:
          cbRef.current.onSectionHover && cbRef.current.onSectionHover(d.id || null);
          break;
        default:
          break;
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [allowlist, send]);

  // Imperative command surface.
  const commands = {
    setEditMode: useCallback((on) => send(BRIDGE.SET_EDIT, { on: !!on }), [send]),
    highlight: useCallback((id) => send(BRIDGE.HIGHLIGHT, { id: id || null }), [send]),
    flash: useCallback((id) => send(BRIDGE.FLASH, { id }), [send]),
    scrollTo: useCallback((id) => send(BRIDGE.SCROLL_TO, { id }), [send]),
    applyEdit: useCallback((id, op) => send(BRIDGE.APPLY_EDIT, { id, op }), [send]),
    revert: useCallback((id) => send(BRIDGE.REVERT, { id }), [send]),
    revertAll: useCallback(() => send(BRIDGE.REVERT_ALL), [send]),
  };

  return { sections, ready, ...commands };
}

export default useSectionBridge;
